/**
 * DslService
 *
 * Frontend wrappers around the DSL pipeline:
 *   - generateDsl: POST /api/get_dsl (chatbot app) — NL → DSL string.
 *   - executeDsl:  POST {rustApi}/dsl/execute (rust-backend) — DSL → result payload.
 *
 * executeDsl bypasses the shared requestJson helper because the Rust endpoint
 * returns a structured DslErrorPayload with HTTP 422 on DSL errors, and we
 * want to preserve that structure instead of collapsing it into an ApiError.
 */

import config from "../config/api";
import { requestJson } from "./api/client";

export interface DslRequest {
  request: string;
}

export interface DslResponse {
  dsl: string;
}

export interface DslVariableInput {
  dates: string[];
  values: number[];
}

export interface DslExecuteRequest {
  expression: string;
  variables?: Record<string, DslVariableInput>;
}

export type DslResultPayload =
  | { type: "time_series"; dates: string[]; values: Array<number | null> }
  | { type: "scalar"; value: number }
  | { type: "text"; value: string }
  | { type: "null" };

export interface DslErrorPayload {
  category: string;
  code: string;
  message: string;
  offset?: number;
  hint?: string;
}

export interface DslExecuteResponse {
  expression: string;
  result: DslResultPayload | null;
  error: DslErrorPayload | null;
}

export const generateDsl = async (request: string, signal?: AbortSignal): Promise<string> => {
  const trimmed = request.trim();
  if (!trimmed) {
    throw new Error("Request must be a non-empty string.");
  }

  const response = await requestJson<DslResponse, DslRequest>(
    "POST",
    "/api/get_dsl",
    { request: trimmed },
    signal
  );

  return response.dsl;
};

export const executeDsl = async (
  expression: string
): Promise<DslExecuteResponse> => {
  const trimmed = expression.trim();
  if (!trimmed) {
    throw new Error("DSL expression must be a non-empty string.");
  }

  const url = `${config.rustApiUrl}/dsl/execute`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expression: trimmed } satisfies DslExecuteRequest),
    });
  } catch (caught: unknown) {
    const detail =
      caught instanceof Error ? caught.message : "Unknown network error.";
    throw new Error(
      `Failed to reach DSL execute endpoint (${url}). ${detail} ` +
        "Ensure the Rust backend is running and /rust proxy routing is configured."
    );
  }

  const payloadText = await response.text();

  if (response.status !== 200 && response.status !== 422) {
    throw new Error(
      `DSL execute failed with HTTP ${response.status}${
        payloadText ? `: ${payloadText}` : ""
      }`
    );
  }

  if (!payloadText) {
    throw new Error(
      `DSL execute returned an empty body with HTTP ${response.status}.`
    );
  }

  let parsed: DslExecuteResponse;
  try {
    parsed = JSON.parse(payloadText) as DslExecuteResponse;
  } catch {
    throw new Error(
      `DSL execute returned an unparseable response (HTTP ${response.status}).`
    );
  }

  return parsed;
};
